import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsData } from '../../mocks/projects';

export default function ProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [typedDescription, setTypedDescription] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const project = id ? projectsData[parseInt(id) as keyof typeof projectsData] : null;

  useEffect(() => {
    if (!project) {
      navigate('/');
      return;
    }

    // Show modal with animation
    setTimeout(() => {
      setShowModal(true);
      setIsLoaded(true);
    }, 100);

    // Typing effect for description
    if (project.longDescription) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev < project.longDescription.length) {
            setTypedDescription(project.longDescription.slice(0, prev + 1));
            return prev + 1;
          }
          clearInterval(timer);
          return prev;
        });
      }, 15); // Adjust speed as needed

      return () => clearInterval(timer);
    }
  }, [project, navigate]);

  const handleClose = () => {
    setShowModal(false);
    setTimeout(() => navigate('/'), 300);
  };

  if (!project) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-500 ${showModal ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div className={`relative w-full max-w-6xl max-h-[90vh] mx-4 bg-gray-900 border border-green-500/30 rounded-lg overflow-hidden transition-all duration-500 ${showModal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        {/* Matrix rain background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute text-green-500/10 font-mono text-xs animate-matrix"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 8}s`,
                animationDuration: `${8 + Math.random() * 4}s`
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="relative z-10 bg-gray-800/80 backdrop-blur-md border-b border-green-500/30 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="text-green-400 text-sm font-mono">
                <span className="text-green-500 animate-pulse">$</span> cat project_{project.id}.json
              </div>
            </div>
            
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-green-400 transition-colors duration-300 cursor-pointer hover-scale"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Project Hero */}
          <div className={`p-8 transition-all duration-1000 ${isLoaded ? 'animate-slide-in-up' : 'opacity-0'}`}>
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Left Column - Project Info */}
              <div>
                {/* Status Badge */}
                {project.featured && (
                  <div className="inline-block bg-gradient-to-r from-green-500/30 to-emerald-500/30 text-green-300 text-sm px-4 py-2 rounded-full border border-green-500/50 mb-4 animate-glow shadow-md shadow-green-500/30">
                    <i className="ri-star-fill mr-2 text-green-400"></i>
                    Featured Project
                  </div>
                )}

                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 gradient-text">
                  {project.title}
                </h1>

                <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-6">
                  <div className="flex items-center">
                    <i className="ri-calendar-line mr-2 animate-pulse"></i>
                    <span>{project.startDate} - {project.endDate}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line mr-2 animate-pulse"></i>
                    <span>{project.teamSize} developer{project.teamSize > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-code-line mr-2 animate-pulse"></i>
                    <span>{project.codeLines.toLocaleString()} lines</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2 animate-pulse"></i>
                    <span className="text-green-400">{project.status}</span>
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-green-400 mb-3">Technology Stack</h3>
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech, index) => (
                      <span
                        key={index}
                        className="bg-green-500/10 text-green-400 px-3 py-1 rounded-full text-sm border border-green-500/20 hover:bg-green-500/20 transition-all duration-300 animate-float"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  <a
                    href={project.github}
                    className="bg-green-500/20 hover:bg-green-500/30 text-green-400 px-6 py-3 rounded-lg border border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 whitespace-nowrap cursor-pointer hover-scale flex items-center"
                  >
                    <i className="ri-github-line mr-2"></i>
                    View Source
                  </a>
                  <a
                    href={project.demo}
                    className="bg-gray-700/50 hover:bg-gray-700/70 text-gray-300 px-6 py-3 rounded-lg border border-gray-600/50 transition-all duration-300 whitespace-nowrap cursor-pointer hover-scale flex items-center"
                  >
                    <i className="ri-external-link-line mr-2"></i>
                    Live Demo
                  </a>
                  <a
                    href={project.documentation}
                    className="bg-gray-700/50 hover:bg-gray-700/70 text-gray-300 px-6 py-3 rounded-lg border border-gray-600/50 transition-all duration-300 whitespace-nowrap cursor-pointer hover-scale flex items-center"
                  >
                    <i className="ri-book-line mr-2"></i>
                    Documentation
                  </a>
                </div>
              </div>

              {/* Right Column - Project Image */}
              <div className="lg:order-last">
                <div className="rounded-lg overflow-hidden border border-green-500/30 hover:border-green-500/50 transition-all duration-500">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Typed Description */}
          <div className={`px-8 pb-8 transition-all duration-1000 ${isLoaded ? 'animate-slide-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
            <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-green-400 mb-4 flex items-center">
                <i className="ri-file-text-line mr-2 animate-pulse"></i>
                Project Overview
              </h3>
              <div className="text-gray-300 leading-relaxed font-mono text-sm">
                {typedDescription}
                {currentIndex < project.longDescription.length && (
                  <span className="animate-pulse text-green-400">|</span>
                )}
              </div>
            </div>
          </div>

          {/* Features, Challenges, Results */}
          <div className={`px-8 pb-8 transition-all duration-1000 ${isLoaded ? 'animate-slide-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Features */}
              <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-lg font-bold text-green-400 mb-4 flex items-center">
                  <i className="ri-star-line mr-2 animate-pulse"></i>
                  Key Features
                </h3>
                <ul className="space-y-2">
                  {project.features.map((feature, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-start">
                      <i className="ri-check-line text-green-400 mr-2 mt-0.5 flex-shrink-0"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Challenges */}
              <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-lg font-bold text-green-400 mb-4 flex items-center">
                  <i className="ri-lightbulb-line mr-2 animate-pulse"></i>
                  Challenges
                </h3>
                <ul className="space-y-2">
                  {project.challenges.map((challenge, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-start">
                      <i className="ri-arrow-right-line text-green-400 mr-2 mt-0.5 flex-shrink-0"></i>
                      {challenge}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Results */}
              <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-lg font-bold text-green-400 mb-4 flex items-center">
                  <i className="ri-trophy-line mr-2 animate-pulse"></i>
                  Results
                </h3>
                <ul className="space-y-2">
                  {project.results.map((result, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-start">
                      <i className="ri-medal-line text-green-400 mr-2 mt-0.5 flex-shrink-0"></i>
                      {result}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Terminal Footer */}
          <div className={`px-8 pb-8 transition-all duration-1000 ${isLoaded ? 'animate-slide-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.9s' }}>
            <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-4 backdrop-blur-sm">
              <div className="text-green-400 text-sm font-mono">
                <span className="text-green-500">$</span> git clone {project.github}
                <br />
                <span className="text-green-500">$</span> cd {project.title.toLowerCase().replace(/\s+/g, '-')}
                <br />
                <span className="text-green-500">$</span> make build && ./run
                <br />
                <span className="text-gray-400"># Project successfully loaded! 🚀</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}